import { Package, Navigation, MapPin, Camera, Zap, Box, CheckSquare, LogOut } from 'lucide-react';
import { normalizeStaffRole, OPERATIONAL_ROLES } from './staffRoles';

export const crewRole = (assignmentRole?: string | null, profileRole?: string | null) =>
  normalizeStaffRole(assignmentRole?.trim() || profileRole);

const isSupport = (role: string) => role === 'Event Attendant' || /assistant|casual/i.test(role);
export const hasOperationalRole = (role: string) => OPERATIONAL_ROLES.includes(normalizeStaffRole(role)) || /assistant|casual/i.test(role);

export function withCrewRole(roster: any, id: string, role: string) {
  if (role && !OPERATIONAL_ROLES.includes(role)) throw new Error('Invalid operational role');
  return { ...roster, crew: roster.crew.map((member: any) => member.id === id ? { ...member, role } : member) };
}

export function getCrewChecklist(role: string, shift: { setupTime?: string; startTime?: string; packDownTime?: string; travel_arrangements?: string; equipmentRevision?: number; equipmentRequired?: boolean }) {
  const normalized = normalizeStaffRole(role);
  if (!hasOperationalRole(normalized)) return [];
  const support = isSupport(normalized);
  const steps: { id: string; label: string; icon: typeof Package; extra?: string; subLabel?: string; requiresPhoto?: boolean }[] = [];
  const gearRequired = (shift.equipmentRevision || 0) > 0 ? shift.equipmentRequired !== false : !!shift.setupTime;
  if (!support && gearRequired) steps.push({ id: 'GEAR_LOADED', label: 'Gear Collected & Loaded', icon: Package });
  steps.push({ id: 'EN_ROUTE', label: 'En Route / In Transit', icon: Navigation, extra: shift.travel_arrangements });
  steps.push({ id: 'ARRIVED', label: 'Arrived on Site', icon: MapPin });
  if (!support && shift.setupTime) steps.push({ id: 'SETUP_COMPLETE', label: 'Setup Complete', subLabel: '(Upload setup photos and test photos)', requiresPhoto: true, icon: Camera });
  // Preserve the historical Assistant/Casual sequence; Attendants now include Event Live.
  if (shift.startTime && (!support || normalized === 'Event Attendant')) steps.push({ id: 'EVENT_LIVE', label: 'Event Live', icon: Zap });
  // No reliable social-capture responsibility field exists: never require it by default.
  if (!support && shift.packDownTime) {
    steps.push({ id: 'PACK_DOWN', label: 'Pack Down Complete', icon: Box });
    steps.push({ id: 'GEAR_RETURNED', label: 'Gear Returned to Base', icon: CheckSquare });
  } else {
    steps.push({ id: 'SHIFT_END', label: support ? 'Shift Complete / Released' : 'End of Shift (Gear Remains on Site)', icon: LogOut });
  }
  return steps;
}

export const nextCrewStepIndex = (steps: { id: string }[], history: readonly string[]) =>
  steps.findIndex(step => !history.includes(step.id));

export function crewArrivalDeadline(role: string, callTime?: string, setupTime?: string, startTime?: string) {
  const valid = (value?: string) => value && /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value) ? value : undefined;
  return valid(callTime) || (hasOperationalRole(role) ? (isSupport(normalizeStaffRole(role)) ? valid(startTime) : valid(setupTime) || valid(startTime)) : undefined);
}
